import { vec2, vec3, vec4 } from 'gl-matrix';
import { calculateNormal } from '../math';
import { Vertex } from './Vertex';

export class Face {
  public vertices: [Vertex, Vertex, Vertex];
  public normal: vec3;
  public textureOffsets: Map<Vertex, vec2>;

  constructor(a: Vertex, b: Vertex, c: Vertex) {
    this.vertices = [a, b, c];
    this.textureOffsets = new Map<Vertex, vec2>();

    for (const v of this.vertices) {
      if (v.partOfFaces.indexOf(this) === -1) {
        v.partOfFaces.push(this);
      }
    }

    // Cross-product normal (not normalized) — preserved from the original
    // implementation. The current callers (unit cubes and pre-normalled
    // sphere vertices) tolerate this; intersectsFace only uses the direction.
    this.normal = calculateNormal(
      this.vertices[0].position,
      this.vertices[1].position,
      this.vertices[2].position,
    );
  }

  public recalculateNormal(): Face {
    this.normal = calculateNormal(
      this.vertices[0].position,
      this.vertices[1].position,
      this.vertices[2].position,
    );
    return this;
  }

  // Copy this face's normal onto each of its vertices (flat shading).
  public useFlatNormals(): Face {
    for (const v of this.vertices) {
      v.setNormal(this.normal);
    }
    return this;
  }

  public setColor(color: vec4 | number[]): Face {
    for (const v of this.vertices) {
      v.setColor(color);
    }
    return this;
  }

  public setTextureOffsets(
    offsetA: vec2 | number[],
    offsetB: vec2 | number[],
    offsetC: vec2 | number[],
  ): Face {
    this.textureOffsets.set(this.vertices[0], offsetA as vec2);
    this.textureOffsets.set(this.vertices[1], offsetB as vec2);
    this.textureOffsets.set(this.vertices[2], offsetC as vec2);
    return this;
  }

  public setTextureOffset(vertex: Vertex, offset: vec2 | number[]): Face {
    this.textureOffsets.set(vertex, offset as vec2);
    return this;
  }

  public getTextureOffset(vertex: Vertex): vec2 | undefined {
    return this.textureOffsets.get(vertex);
  }

  public getCenter(): vec3 {
    const center = vec3.create();
    for (const v of this.vertices) {
      vec3.add(center, center, v.position);
    }
    vec3.scale(center, center, 1 / this.vertices.length);
    return center;
  }
}
